import * as React from "react"
import { ReactElement, useEffect, useMemo, useState } from "react"
import ApplicationLayout from "@rio-cloud/rio-uikit/ApplicationLayout"
import Notification from "@rio-cloud/rio-uikit/Notification"
import Spinner from "@rio-cloud/rio-uikit/Spinner"
import { FormattedMessage } from "react-intl"
import { routingConfig } from "../Application/RoutingConfig"
import {
    ShipmentConfirmation,
    TransportOrder,
    TransportOrderType
} from "../../model/transportorder/transportOrder"
import { MaterialOrderConfirmationDialog } from "./confirmation/MaterialOrderConfirmationDialog"
import { EmptiesOrderConfirmationDialog } from "./confirmation/EmptiesOrderConfirmationDialog"
import { TableToolBar } from "./TransportOrderTable/TableToolBar"
import { AnnouncementApplicationBanner } from "../Application/AnnouncementApplicationBanner"
import {
    useFilterTransportOrdersByCargoType,
    useSearchTransportOrders,
    useSortTransportOrders
} from "../../redux/transportorder.hooks"
import { useLocation, useNavigate } from "react-router"
import { TransportOrderTable } from "./TransportOrderTable/TransportOrderTable"
import { getCsvTransportOrders, getOverviewTransportOrders } from "../../redux/transportorder.selector"
import moment from "moment"
import { useAppDispatch, useAppSelector } from "../../redux/store"
import {
    extractTableParamsFromUrl,
    mapSortByToQueryString,
    SortBy,
    SortCriteria,
    SortDirection,
    TransportOrderDateFilterType,
    transportOrderSlice
} from "../../redux/transportorder.slice"
import { getTransportOrderLoadingSpinnerState } from "../../redux/ui.selector"
import queryString from "query-string"
import {
    TransportOrderBundleFetchParams,
    useGetTransportOrderBundlesQuery,
    usePostFreightForwarderResponseMutation
} from "../../client/transportorder-hub/transportOrderHubApi"
import { config } from "../../configuration/app/app-config"
import { OrderOverviewReturnState, useOrderOverviewInfiniteRendering } from "./useOrderOverviewInfiniteRendering"

export interface ColumnDetails {
    label?: string
    width?: number | string
    minWidth?: number | string
    defaultWidth?: number
    maxWidth?: number
    sortBy?: SortBy
    alternateHead?: React.ReactElement
    additionalStyles?: string
}

const mapQueryStringToDate = (str: string | undefined, defaultValue: Date): Date => {
    if (str === undefined || str === "") {
        return defaultValue
    }
    const date = new Date(str)
    return isFinite(date.getTime()) ? date: defaultValue
}

const useUpdateHistoryWithTableParams = () => {
    const navigate = useNavigate()
    const location = useLocation()

    const tableParams = extractTableParamsFromUrl(location.search)

    interface TableQueryParams {
        searchPhrase: string | undefined
        sortBy: string | undefined
        sortDir: SortDirection
        orderDateTo: string | undefined
        orderDateFrom: string | undefined
        dateFilterType: TransportOrderDateFilterType
        cargoTypes?: TransportOrderType[]
    }

    const tableQueryParams: TableQueryParams = {
        searchPhrase: tableParams.searchPhrase,
        sortBy: mapSortByToQueryString(tableParams.sortBy),
        sortDir: tableParams.sortDirection,
        orderDateFrom: tableParams.orderDateFrom,
        orderDateTo: tableParams.orderDateTo,
        dateFilterType: tableParams.dateFilterType,
        cargoTypes: tableParams.cargoTypes,
    }

    return (input: Partial<TableQueryParams>) => {
        const newTableQueryParams = { ...tableQueryParams, ...input }
        if (newTableQueryParams.sortBy === undefined) {
            delete newTableQueryParams.sortBy
        }
        if (newTableQueryParams.searchPhrase === "" || newTableQueryParams.searchPhrase === undefined) {
            delete newTableQueryParams.searchPhrase
        }
        if (newTableQueryParams.cargoTypes?.length === 0) {
            delete newTableQueryParams.cargoTypes
        }
        navigate({ search: queryString.stringify(newTableQueryParams) })
    }
}

const mapDateRangeToFetchParams = (
    dateFilterType: TransportOrderDateFilterType,
    dateFrom: Date,
    dateTo: Date,
): TransportOrderBundleFetchParams => {
    const fromIso = dateFrom.toISOString()
    const toIso = dateTo.toISOString()

    switch (dateFilterType) {
    case TransportOrderDateFilterType.EARLIEST_PICKUP_AT:
        return { fromEarliestPickupAt: fromIso, toEarliestPickupAt: toIso }
    case TransportOrderDateFilterType.LATEST_DELIVERY_AT:
        return { fromLatestDeliveryAt: fromIso, toLatestDeliveryAt: toIso }
    default:
        return { fromCreatedAt: fromIso, toCreatedAt: toIso }
    }
}

export const OrderOverview = (): ReactElement => {
    const location = useLocation()
    const tableParams = useMemo(() => extractTableParamsFromUrl(location.search), [location.search])
    const navigationState = location.state as Partial<OrderOverviewReturnState> | null
    const overviewReturnState = typeof navigationState?.visibleCount === "number"
        ? navigationState as OrderOverviewReturnState
        : undefined
    const initialOrderDateFrom = mapQueryStringToDate(tableParams.orderDateFrom, moment().subtract(14, "days").startOf("day").toDate())
    const initialOrderDateTo = mapQueryStringToDate(tableParams.orderDateTo, moment().add(14, "day").endOf("day").toDate())
    const activeDateFilterType = tableParams.dateFilterType
    const [orderDateFrom, setOrderDateFrom] = useState<Date>(initialOrderDateFrom)
    const [orderDateTo, setOrderDateTo] = useState<Date>(initialOrderDateTo)
    const { data } = useGetTransportOrderBundlesQuery(
        mapDateRangeToFetchParams(activeDateFilterType, orderDateFrom, orderDateTo),
        { pollingInterval: config.transportOrderApi.pollIntervalMillis }
    )
    const [postFreightForwarderResponseMutation] = usePostFreightForwarderResponseMutation()
    const dispatch = useAppDispatch()

    // This is taking a roundtrip to the redux store, where searching and sorting is done.
    // One might consider a more direct approach, but this is the way it was done before.
    useEffect(() => {
        dispatch(transportOrderSlice.actions.setTransportOrderBundles({
            items: data ?? [],
            totalItems: data?.length ?? 0
        }))
    }, [data, dispatch])

    const isFetchingTransportOrders = useAppSelector(getTransportOrderLoadingSpinnerState)
    const [transportOrderToConfirm, setTransportOrderToConfirm] = useState<TransportOrder | undefined>(undefined)
    const navigate = useNavigate()
    const searchTransportOrders = useSearchTransportOrders()
    const sortTransportOrders = useSortTransportOrders()
    const filterTransportOrdersByCargoType = useFilterTransportOrdersByCargoType()
    const updateHistoryWithTableParams = useUpdateHistoryWithTableParams()
    const transportOrders = useAppSelector(getOverviewTransportOrders)
    const csvTransportOrders = useAppSelector(getCsvTransportOrders)
    const {
        effectiveVisibleCount,
        visibleTransportOrders,
        scrollLoadTriggerTransportOrderId,
        setScrollLoadTriggerElement,
        resetVisibleCount,
    } = useOrderOverviewInfiniteRendering({
        transportOrders,
        overviewReturnState,
        isFetchingTransportOrders: Boolean(isFetchingTransportOrders),
    })

    useEffect(() => {
        if (location.search) {
            const tableParams = extractTableParamsFromUrl(location.search)
            searchTransportOrders(tableParams.searchPhrase)
            sortTransportOrders({
                sortBy: tableParams.sortBy,
                sortDirection: tableParams.sortDirection
            })
        } else {
            searchTransportOrders(undefined)
            sortTransportOrders(undefined)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderDateTo, orderDateFrom])

    // search TOs every time the search phrase changes
    useEffect(() => {
        searchTransportOrders(tableParams.searchPhrase)
    }, [searchTransportOrders, tableParams.searchPhrase])

    // sort transportOrders every time sortBy or sortDirection changes
    useEffect(() => {
        sortTransportOrders({
            sortBy: tableParams.sortBy,
            sortDirection: tableParams.sortDirection
        })
    }, [sortTransportOrders, tableParams.sortBy, tableParams.sortDirection])

    useEffect(() => {
        filterTransportOrdersByCargoType(tableParams.cargoTypes)
    }, [filterTransportOrdersByCargoType, tableParams.cargoTypes])

    const updateSortBy = (sortBy: SortBy): void => {
        const tableParams = extractTableParamsFromUrl(location.search)
        const currentSort = { sortBy: tableParams.sortBy, sortDirection: tableParams.sortDirection }
        const reverseOrder = (criteria: SortCriteria): SortCriteria => {
            return {
                ...criteria,
                sortDirection: criteria.sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC
            }
        }
        const sortCriteria: SortCriteria = currentSort && currentSort.sortBy === sortBy
            ? reverseOrder(currentSort)
            : { sortBy: sortBy, sortDirection: SortDirection.ASC }

        updateHistoryWithTableParams({
            sortBy: mapSortByToQueryString(sortCriteria.sortBy),
            sortDir: sortCriteria.sortDirection,
        })
    }

    const openConfirmationDialog = (transportOrder: TransportOrder): Promise<void> => {
        return new Promise<void>(resolve => {
            setTransportOrderToConfirm(transportOrder)
            resolve()
        })
    }

    const jumpToDetailView = (transportOrder: TransportOrder): void => {
        const search = queryString.stringify({ redirect_uri: location.pathname + location.search })

        navigate({
            pathname: routingConfig.transportOrderPresenter.path.replace(":id", transportOrder.id),
            search,
        }, {
            state: {
                scrollPositionToId: transportOrder.id,
                visibleCount: effectiveVisibleCount,
            } satisfies OrderOverviewReturnState,
        })
    }

    const provideCorrespondingConfirmationDialog = (transportOrder: TransportOrder): ReactElement => {
        switch (transportOrder.orderType) {
        case TransportOrderType.MATERIAL:
            return <MaterialOrderConfirmationDialog
                show={true}
                transportOrder={transportOrder}
                onCancel={hideOrderConfirmationDialog}
                onConfirm={applyConfirmations}
                toDetailsButtonCallback={(): void => jumpToDetailView(transportOrder)}
            />
        case TransportOrderType.EMPTIES:
            return <EmptiesOrderConfirmationDialog
                show={true}
                transportOrder={transportOrder}
                onCancel={hideOrderConfirmationDialog}
                onConfirm={applyConfirmations}
            />
        }
    }

    const hideOrderConfirmationDialog = (): void => setTransportOrderToConfirm(undefined)

    const applyConfirmations = (shipmentConfirmations: ShipmentConfirmation[]): Promise<void> => {
        if (transportOrderToConfirm === undefined) {
            return Promise.reject("Internal error: no transport order to confirm")
        }
        const confirmationPromises = shipmentConfirmations.map(it => postFreightForwarderResponseMutation(it).unwrap())
        const promiseForAll = Promise.all(confirmationPromises)
        return promiseForAll
            .then(hideOrderConfirmationDialog)
            .catch((err) => {
                Notification.error(<FormattedMessage id={"transportOrder.error.confirm"}/>)
                return Promise.reject(err)
            })
    }

    const updateSearchPhraseFilter = (value: string): void => {
        resetVisibleCount()
        updateHistoryWithTableParams({ searchPhrase: value })
    }

    const updateCargoTypeFilter = (cargoTypes: TransportOrderType[]): void => {
        resetVisibleCount()
        updateHistoryWithTableParams({ cargoTypes })
    }

    const changeOrderDateFrom = (date: Date): void => {
        const newDateFrom = moment(date).startOf("day").toDate()
        setOrderDateFrom(newDateFrom)
        updateHistoryWithTableParams({ orderDateFrom: newDateFrom.toISOString(), orderDateTo: orderDateTo.toISOString() })
    }

    const changeOrderDateTo = (date: Date): void => {
        const newDateTo = moment(date).endOf("day").toDate()
        setOrderDateTo(newDateTo)
        updateHistoryWithTableParams({ orderDateTo: newDateTo.toISOString(), orderDateFrom: orderDateFrom.toISOString() })
    }

    const changeDateFilterType = (dateFilterType: TransportOrderDateFilterType): void => {
        updateHistoryWithTableParams({
            dateFilterType,
            orderDateFrom: orderDateFrom.toISOString(),
            orderDateTo: orderDateTo.toISOString(),
        })
    }

    return (
        <ApplicationLayout.Body banner={<AnnouncementApplicationBanner/>}>
            <TableToolBar
                location={location.search}
                activeDateFilterType={activeDateFilterType}
                onDateFilterTypeChange={changeDateFilterType}
                onSearchPhraseChange={updateSearchPhraseFilter}
                cargoTypes={tableParams.cargoTypes}
                onCargoTypesChange={updateCargoTypeFilter}
                orderDateFrom={orderDateFrom}
                onChangeOrderDateFrom={changeOrderDateFrom}
                orderDateTo={orderDateTo}
                onChangeOrderDateTo={changeOrderDateTo}
                transportOrders={csvTransportOrders}
            />
            {isFetchingTransportOrders
                ? <Spinner isFullSized/>
                : <TransportOrderTable
                    transportOrders={visibleTransportOrders}
                    onSortByChanged={updateSortBy}
                    onDetailsClicked={jumpToDetailView}
                    onConfirmClicked={openConfirmationDialog}
                    scrollLoadTriggerTransportOrderId={scrollLoadTriggerTransportOrderId}
                    onScrollLoadTriggerRowRendered={setScrollLoadTriggerElement}
                />
            }
            {transportOrderToConfirm
                ? provideCorrespondingConfirmationDialog(transportOrderToConfirm)
                : null}
        </ApplicationLayout.Body>
    )
}
